/** @jsxImportSource preact */
/** 图标全部内联：Shadow DOM 里不加载外部资源，也不引图标库进单文件包。 */
interface StrokeIconProps {
  size: number;
  strokeWidth?: number;
}

/** 返回类型交给推断：Preact 的 JSX.SVGAttributes 已标记废弃。 */
function strokeProps(size: number, strokeWidth: number) {
  return {
    fill: "none",
    height: size,
    stroke: "currentColor",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    "stroke-width": strokeWidth,
    viewBox: "0 0 24 24",
    width: size,
  } as const;
}

/** 出错。 */
export function AlertIcon({ size, strokeWidth = 1.9 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5" />
      <path d="M12 16.2v.01" />
    </svg>
  );
}

/** 清空会话。 */
export function RestartIcon({ size, strokeWidth = 1.8 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 2.6-6.4L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

export function CloseIcon({ size, strokeWidth = 1.8 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export function FullscreenIcon({
  size,
  strokeWidth = 1.8,
  active,
}: StrokeIconProps & { active: boolean }) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path
        d={
          active
            ? "M9 3v6H3m12-6v6h6M9 21v-6H3m12 6v-6h6"
            : "M9 3H3v6m12-6h6v6M3 15v6h6m12-6v6h-6"
        }
      />
    </svg>
  );
}

export function SendIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  );
}

/** 语音模式：麦克风开关。 */
export function MicIcon({ size, strokeWidth = 1.9 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <rect height="10" rx="4" width="6" x="9" y="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3M8 21h8" />
    </svg>
  );
}

/** 语音模式错误或关闭时的麦克风。 */
export function MicOffIcon({ size, strokeWidth = 1.9 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path d="M9 9v2a3 3 0 0 0 5 2.24M15 9V7a3 3 0 0 0-5.12-2.12M5 5l14 14" />
      <path d="M5 11a7 7 0 0 0 11.2 5.6M12 18v3M8 21h8" />
    </svg>
  );
}

/** 发送键忙的时候顶替 SendIcon。转的是整个 svg，关键帧见 animations.ts。 */
export function SpinnerIcon({ size, strokeWidth = 2.4 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ animation: "gnSpin .75s linear infinite" }}
    >
      <circle cx="12" cy="12" opacity=".3" r="9" />
      <path d="M21 12a9 9 0 0 0-9-9" />
    </svg>
  );
}

export function ArrowRightIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

export function ArrowDownIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path d="M12 5v14m-7-7 7 7 7-7" />
    </svg>
  );
}

/** 窥视条右侧的展开箭头。 */
export function ChevronUpIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path d="m6 15 6-6 6 6" />
    </svg>
  );
}

/** 请求卡上的信封。 */
export function MailIcon({ size, strokeWidth = 1.8 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <rect height="14" rx="2" width="18" x="3" y="5" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

/** 消息操作行的复制。 */
export function CopyIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <rect height="11" rx="2" width="11" x="9" y="9" />
      <path d="M5 15V6a2 2 0 0 1 2-2h9" />
    </svg>
  );
}

/** 点赞；点踩是同一个图形倒过来。选中后实心。 */
export function ThumbIcon({
  size,
  strokeWidth = 2,
  down = false,
  filled = false,
}: StrokeIconProps & { down?: boolean; filled?: boolean }) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      fill={filled ? "currentColor" : "none"}
      style={{
        display: "block",
        transform: down ? "rotate(180deg)" : undefined,
        transition: "fill .2s",
      }}
    >
      <path d="M7 11v9H4v-9h3zm0 0 4-8a2 2 0 0 1 3 2v4h5a2 2 0 0 1 2 2l-1.5 7a2 2 0 0 1-2 2H7" />
    </svg>
  );
}

/** 复制成功后顶替 CopyIcon。 */
export function CheckIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

/** 已取消。 */
export function CircleXIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </svg>
  );
}

/** 分包裹发货。 */
export function PackageIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" />
      <path d="M12 22V12" />
      <path d="m3.3 7 8.7 5 8.7-5" />
    </svg>
  );
}

/** 在新页面打开。 */
export function ExternalLinkIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

/** 账号：登录店铺账号。 */
export function UserIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

/** 锁：内容被收起、只读。 */
export function LockIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <rect height="11" rx="2" width="18" x="3" y="11" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** 盾牌加对勾：重新核验身份。 */
export function ShieldCheckIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

/** 放大镜：没查到。 */
export function SearchIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

/** 三角警示：我们这边出了问题。 */
export function TriangleAlertIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/** 纸飞机：发送请求、请求已发出。 */
export function PaperPlaneIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
      <path d="m21.854 2.147-10.94 10.939" />
    </svg>
  );
}

/** 圆圈问号：结果还不确定。 */
export function CircleHelpIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/** 禁止：邮件被退回。 */
export function BanIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m4.9 4.9 14.2 14.2" />
    </svg>
  );
}

/** 地球：商家的联系页。 */
export function GlobeIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </svg>
  );
}

/** 听筒：商家的电话。 */
export function PhoneIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

/** 圆圈 i：提示行。 */
export function InfoIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}

/** 带字的文档：一份还没发出的请求。 */
export function FileTextIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </svg>
  );
}

/** 两道弧加箭头：重试。 */
export function RefreshIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

/** 下拉框右侧的展开箭头；展开后换成 ChevronUpIcon。 */
export function ChevronDownIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/** 铅笔：买家自己填的内容。 */
export function PencilIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    </svg>
  );
}

/** 标题栏圆按钮：最小化成收起入口。 */
export function MinimizeIcon({ size, strokeWidth = 1.9 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path d="M7 15.5h10" />
    </svg>
  );
}

/** 标题栏圆按钮：聊天窗展开到空间（两角向外），或从空间收回聊天窗（两角向内）。 */
export function SpaceToggleIcon({
  size,
  strokeWidth = 1.9,
  open,
}: StrokeIconProps & { open: boolean }) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      {open ? (
        <path d="M20 10h-6V4M14 10l7-7M4 14h6v6M10 14l-7 7" />
      ) : (
        <path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" />
      )}
    </svg>
  );
}

/** 空间顶部的购物袋。 */
export function BagIcon({ size, strokeWidth = 1.8 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d="M5 8h14l-1 12H6z" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    </svg>
  );
}

/** 数量步进器的减号；加号用下面的 PlusIcon。 */
export function MinusIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg {...strokeProps(size, strokeWidth)} aria-hidden="true">
      <path d="M6 12h12" />
    </svg>
  );
}

/** 加号：商品卡图片右下角的加入购物车。 */
export function PlusIcon({ size, strokeWidth = 2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ display: "block", flex: "none" }}
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** 右尖括号：订单列表每行右侧的「进入」提示。 */
export function ChevronRightIcon({ size, strokeWidth = 2.2 }: StrokeIconProps) {
  return (
    <svg
      {...strokeProps(size, strokeWidth)}
      aria-hidden="true"
      style={{ display: "block", flex: "none" }}
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}
